import { useRef } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import Select from 'components/Select/Select';
import Textarea from 'components/Textarea/Textarea';
import { MANUFACTURE_CONNECTION_TYPE_OPTIONS } from 'constants/dataSets';
import { COMMENT_MANUFACTURE, MANUFACTURE_CONNECTION_TYPE } from 'constants/formFields';
import { CONNECTION_COMMENT_MAX_LENGTH } from 'constants/general';

import type { FieldPath, FieldValues } from 'react-hook-form';
import type IManufactureConnectionFields from './interfaces/IManufactureConnectionFields';

import styles from './sass/ManufactureConnectionFields.module.scss';

// The editable body of a manufacture connection: which arrangement the plant works under, and a
// free-text note. Counterpart to `GoodsSupplierConnectionFields` — the sourcing panel renders one
// or the other per row, depending on whether the source is a factory.
function ManufactureConnectionFields<TFieldValues extends FieldValues>({
  control,
  fieldName = (field) => field as FieldPath<TFieldValues>,
}: Readonly<IManufactureConnectionFields<TFieldValues>>) {
  const { t } = useTranslation();
  // Anchors the select's absolutely positioned menu, which would otherwise be clipped by
  // whichever scroll container the host renders these fields in.
  const containerRef = useRef<HTMLDivElement>(null);

  return (
    <div className={styles.container} ref={containerRef}>
      <Controller
        control={control}
        name={fieldName(MANUFACTURE_CONNECTION_TYPE)}
        render={({ field: { value, onChange }, fieldState: { error } }) => (
          <Select
            className={styles.field}
            options={MANUFACTURE_CONNECTION_TYPE_OPTIONS}
            placeholder={t('valueChainMap.connectionType')}
            label={t('valueChainMap.connectionType')}
            value={value ?? ''}
            onChange={onChange}
            menuPosition="absolute"
            modalRef={containerRef}
            maxMenuHeight={150}
            error={error?.message}
          />
        )}
      />

      <Controller
        control={control}
        name={fieldName(COMMENT_MANUFACTURE)}
        render={({ field: { name, value, onChange }, fieldState: { error } }) => (
          <Textarea
            name={name}
            value={value as string}
            label={t('valueChainMap.comment')}
            placeholder={t('common.typeYourMessage')}
            // Hard-stopping at the backend's limit rather than letting the field overflow and
            // rejecting on save; the minimum is left to validation, since a half-typed comment
            // should not read as an error.
            onChange={(event) => onChange(event.target.value.slice(0, CONNECTION_COMMENT_MAX_LENGTH))}
            error={error?.message}
            charactersLimit={CONNECTION_COMMENT_MAX_LENGTH}
          />
        )}
      />
    </div>
  );
}

export default ManufactureConnectionFields;
